import { useRef, useState } from "react";
import Button from "../../../../shared/components/Buttons/Button";
import ButtonWithToolTip from "../../../../shared/components/Buttons/ButtonWithToolTip";
import {
  BG_JOB_STATUS,
  LOCK_TYPE,
  orchestratorConstants,
  SPINNER_MESSAGES,
  toastType,
  shopifyConstants,
  EMPTY_STRING,
} from "../../../../constants";
import {
  ResetCartIcon,
  LoadingIndicatorCentered,
  ShowIcon,
} from "../../../../assets/icons/iconsProvider";
import ConfirmationModal from "../../../Inventory/Items/ConfirmationModal/ConfirmationModal";
import { customToast, isOneMinuteOld } from "../../../../shared/utility";

const UpdateInventoryButton = ({
  currentStore,
  toggleResetStatusModal,
  isUpdatingInventoryPresetsToDefault,
  setIsUpdatingInventoryPresetsToDefault,
  dfStartOrchestrator,
  draggableNotification,
  lastBackgroundJobCreatedAt,
  storeMiscData,
  isCompanyStore,
  isResetAllowed,
}) => {
  const [isConfirmationModal, setIsConfirmationModal] = useState(false);
  const isNewResetStartedRef = useRef(false);
  //------- non admin in company store is only disabled, running reset also shows spinner
  const isResetInventoryButtonDisabled =
    !isResetAllowed || isUpdatingInventoryPresetsToDefault;
  const shouldShowSpinner =
    Array.isArray(storeMiscData) &&
    !storeMiscData.some((row) => row.miscData?.status === BG_JOB_STATUS.PAUSED);
  // open/close confirmation modal
  const toggleConfirmationModal = () => {
    setIsConfirmationModal((prev) => !prev);
  };

  // handles clicking the "Update Inventory" button
  const handleUpdateInventoryPresetsToDefault = () => {
    toggleConfirmationModal();
  };

  // confirm button handler
  const handleConfirmYesButtonClick = async () => {
    try {
      const data = {
        storeId: currentStore?.id,
        orchestrator: orchestratorConstants.UPDATE_INVENTORY_PRESETS,
      };
      dfStartOrchestrator(data);
      setIsUpdatingInventoryPresetsToDefault(true);
      toggleConfirmationModal();
      isNewResetStartedRef.current = true;
    } catch (error) {
      customToast(error, toastType.ERROR);
      toggleConfirmationModal();
    }
  };

  // cancel button handler
  const handleConfirmNoButtonClick = () => {
    toggleConfirmationModal();
  };
  return (
    <>
      <ButtonWithToolTip
        IconImage={
          isUpdatingInventoryPresetsToDefault && shouldShowSpinner
            ? LoadingIndicatorCentered
            : ResetCartIcon
        }
        handleClick={handleUpdateInventoryPresetsToDefault}
        isDisabled={isResetInventoryButtonDisabled}
        label="Reset Existing Inventory"
        className="w-100 d-flex justify-content-center align-items-center text-center"
        toolTipRootClassName="w-100"
        toolTipBody={
          isResetAllowed
            ? EMPTY_STRING
            : shopifyConstants.COMPANY_RESET_ADMIN_ONLY
        }
      />
      {/* company store reset status is only for admin */}
      {isResetAllowed && (
        <Button
          IconImage={ShowIcon}
          handleClick={toggleResetStatusModal}
          isDisabled={
            isUpdatingInventoryPresetsToDefault &&
            !draggableNotification?.messages.some(
              (msg) =>
                msg.message === SPINNER_MESSAGES.SHOPIFY_RESET_IN_PROGRESS
            ) &&
            isNewResetStartedRef.current
          }
          label="View Reset Status"
          className="w-100 d-flex justify-content-center align-items-center text-center"
        />
      )}

      <ConfirmationModal
        isConfirmationModal={isConfirmationModal}
        handleNoButtonClick={handleConfirmNoButtonClick}
        handleYesButtonClick={handleConfirmYesButtonClick}
        toggleConfirmationModal={toggleConfirmationModal}
        message={`<h5>Confirm Shopify Configuration Reset for All Inventory Items</h5>
            <p>
                This action will replace all existing <strong>Shopify Configuration Settings</strong> for every item in ${
                  isCompanyStore
                    ? "the inventory of every Store in your Company connected to Shopify"
                    : "your Store inventory"
                }. These settings will be replaced according to the current applicable <strong>Global</strong> and <strong>Custom Shopify Configuration</strong> settings for each item.
            </p>

            <p>This includes the following configurations:</p>
            <ul>
                <li>Link to Shopify</li>
                <li>Separate In-Store &amp; Online Quantities</li>
                <li>All Shopify Field Mappings Settings</li>
            </ul>

            <p>While this update is running:</p>
            <ul>
                <li>
                All <strong>inventory-related actions</strong> in CollectPOS (such as Sales, Trades, Returns, Adding or Editing Inventory, etc.) will be temporarily disabled to prevent conflicts.
                </li>
                <li>
                Your connected Shopify Store’s Website will also be temporarily unavailable.
                </li>
                <li>
                No changes or edits should be made directly on Shopify during the update process to avoid interruptions.
                </li>
            </ul>

            <p>
                It is highly recommended to only execute this action outside of business/operating hours. <strong>Are you sure you want to proceed?</strong>
            </p>`}
        yesButtonText="Confirm"
        noButtonText="Cancel"
        isHtmlMessage={true}
      />
    </>
  );
};

export default UpdateInventoryButton;
